/* eslint-disable @typescript-eslint/no-explicit-any */

import * as z from 'zod';
import { preprocessSort } from './preprocessors';
import type { FilterDefinitions, FiltersToZodShape } from './types';
import { zodTypeFromFilter } from './utils';

export const jsonApiInclude = <const T extends readonly string[]>(
    includes: T
): z.ZodOptional<z.ZodArray<z.ZodEnum<z.util.ToEnum<T[number]>>>> => {
    return z.enum(includes).array().optional();
};

export const jsonApiFields = z
    .record(z.string(), z.string().array())
    .optional();

export const jsonApiFilter = <F extends FilterDefinitions>(
    filters: F
): z.ZodOptional<z.ZodObject<FiltersToZodShape<F>>> => {
    const objShape: FiltersToZodShape<F> = Object.entries(filters).reduce(
        (carrier, [name, filter]) => {
            const zodType = zodTypeFromFilter(filter).optional();

            return {
                ...carrier,
                [name]: zodType,
            };
        },
        {} as any
    );

    return z.object(objShape).optional();
};

export const jsonApiSort = <const T extends readonly string[]>(
    sortable: T
): z.ZodOptional<
    z.ZodPipe<
        z.ZodTransform<unknown, unknown>,
        z.ZodArray<
            z.ZodObject<{
                field: z.ZodEnum<z.util.ToEnum<T[number]>>;
                direction: z.ZodUnion<
                    readonly [z.ZodLiteral<'ASC'>, z.ZodLiteral<'DESC'>]
                >;
            }>
        >
    >
> => {
    const sortObjects = z.array(
        z.object({
            field: z.enum(sortable),
            direction: z.union([z.literal('ASC'), z.literal('DESC')]),
        })
    );

    return z.preprocess(preprocessSort, sortObjects).optional();
};
